import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { logger } from "@/utils/productionLogger";

// Nouveaux types pour le système de pricing
interface SubscriptionFeatures {
  brandingProEnabled: boolean;
  totalSeats: number;
  additionalSeats: number;
  creditBalance: number;
  monthlyRecurringCost: number;
  stripeCustomerId?: string;
  subscriptionStatus: 'active' | 'canceled' | 'past_due' | 'inactive';
}

interface OrganizationData {
  id: string;
  name: string;
  userRole: "owner" | "admin" | "member";
  usedSeats: number;
  features: SubscriptionFeatures;
}

export const useSubscriptionFeatures = () => {
  const [organizationData, setOrganizationData] = useState<OrganizationData | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  logger.debug("useSubscriptionFeatures hook initialized", { userEmail: user?.email, userId: user?.id });

  const fetchOrganizationData = useCallback(async () => {
    logger.debug("fetchOrganizationData called", { userEmail: user?.email || "not logged in" });
    if (!user) {
      logger.debug("No user, setting loading to false");
      setLoading(false);
      return;
    }

    try {
      // Étape 1: Récupérer l'organisation ID depuis le profil de l'utilisateur
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("organization_id")
        .eq("id", user.id)
        .maybeSingle();

      logger.debug("Profile data result", { hasProfile: !!profile, hasError: !!profileError });

      let orgId = profile?.organization_id;

      // Si pas d'org ID dans le profil, essayer via organization_members
      if (!orgId) {
        const { data: memberData, error: memberError } = await supabase
          .from("organization_members")
          .select("organization_id, role, status")
          .eq("user_id", user.id)
          .eq("status", "active")
          .maybeSingle();

        logger.debug("Member data result", { hasMemberData: !!memberData, hasError: !!memberError });
        orgId = memberData?.organization_id;
      }

      if (!orgId) {
        logger.debug("No organization found for user");
        setOrganizationData(null);
        setLoading(false);
        return;
      }

      logger.debug("Organization ID found", { orgId });

      // Étape 2: Récupérer les données de l'organisation
      const { data: orgData, error: orgError } = await supabase
        .from("organizations")
        .select("id, name")
        .eq("id", orgId)
        .maybeSingle();

      logger.debug("Organization data result", { hasOrgData: !!orgData, hasError: !!orgError });

      if (orgError || !orgData) {
        logger.debug("Organization not found or error", { error: orgError });
        setOrganizationData(null);
        setLoading(false);
        return;
      }

      // Étape 3: Récupérer le rôle de l'utilisateur
      const { data: memberData, error: memberError } = await supabase
        .from("organization_members")
        .select("role, status")
        .eq("user_id", user.id)
        .eq("organization_id", orgId)
        .eq("status", "active")
        .maybeSingle();

      logger.debug("Member role result", { hasMemberData: !!memberData, hasError: !!memberError });

      // Étape 4: Récupérer les données depuis organization_features (nouvelle architecture)
      const { data: orgFeaturesData, error: featuresError } = await supabase
        .from("organization_features")
        .select("*")
        .eq("organization_id", orgId)
        .single();

      logger.debug("Organization features data", { hasData: !!orgFeaturesData, hasError: !!featuresError });

      // Si pas de données dans organization_features, fallback vers subscriptions
      let subscription = {};
      if (featuresError || !orgFeaturesData) {
        logger.debug("Fallback to subscriptions table");
        const { data: subscriptionResult, error: subError } = await supabase
          .from("subscriptions")
          .select("*")
          .eq("organization_id", orgId)
          .maybeSingle();
        
        subscription = (subscriptionResult as unknown) as Record<string, unknown> || {};
        logger.debug("Subscription data (fallback)", { hasSubscription: !!subscription, hasError: !!subError });
      }

      // Étape 5: Le solde de crédits sera géré par le hook useCreditBalance
      // Pour éviter les problèmes de types, on initialise à 0
      const creditBalance = 0;
      logger.debug("Credit balance initialized to 0 - will be managed by useCreditBalance hook");

      // Étape 6: Compter les sièges utilisés
      const { count: usedSeats, error: countError } = await supabase
        .from("organization_members")
        .select("id", { count: "exact", head: true })
        .eq("organization_id", orgId)
        .in("status", ["active", "invited"]);

      logger.debug("Used seats count", { usedSeats, hasError: !!countError });

      // Construire les features avec les données de organization_features ou fallback
      const features: SubscriptionFeatures = orgFeaturesData ? {
        // Utiliser les nouvelles données organization_features
        brandingProEnabled: Boolean(orgFeaturesData.branding_pro_enabled),
        totalSeats: Number(orgFeaturesData.total_seats),
        additionalSeats: Number(orgFeaturesData.additional_seats),
        creditBalance: creditBalance,
        monthlyRecurringCost: Number(orgFeaturesData.monthly_total_cost ?? 0),
        stripeCustomerId: orgFeaturesData.stripe_customer_id,
        subscriptionStatus: 'active' // Calculé depuis les données Stripe
      } : {
        // Fallback vers les anciennes données subscriptions
        brandingProEnabled: Boolean(subscription?.branding_pro_enabled ?? subscription?.branding_enabled ?? false),
        totalSeats: Number(subscription?.base_seats ?? 1) + Number(subscription?.additional_seats ?? 0),
        additionalSeats: Number(subscription?.additional_seats ?? 0),
        creditBalance: creditBalance,
        monthlyRecurringCost: Number(subscription?.monthly_recurring_total ?? 0),
        stripeCustomerId: subscription?.stripe_customer_id as string | undefined,
        subscriptionStatus: (subscription?.status as 'active' | 'canceled' | 'past_due' | 'inactive') ?? 'inactive'
      };

      const finalOrgData = {
        id: orgId,
        name: orgData.name,
        userRole: (memberData?.role as "owner" | "admin" | "member") ?? "member",
        usedSeats: usedSeats ?? 0,
        features,
      };
      
      logger.debug("Organization data loaded", { 
        orgId: finalOrgData.id, 
        usedSeats: finalOrgData.usedSeats,
        creditBalance: features.creditBalance,
        monthlyRecurringCost: features.monthlyRecurringCost
      });
      
      setOrganizationData(finalOrgData);
    } catch (_error) {
      logger.error("Error fetching organization data", _error as Error);
      setOrganizationData(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchOrganizationData();

    // Écouter les changements en temps réel sur la table organizations
    if (user) {
      const channel = supabase
        .channel('organization-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'organizations'
          },
          (payload) => {
            logger.debug('Organization changed, refreshing data', payload);
            fetchOrganizationData();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user, fetchOrganizationData]);

  // Permissions calculées
  const canManageMembers = organizationData?.userRole === "owner" || organizationData?.userRole === "admin";
  const canManageBranding = canManageMembers && organizationData?.features.brandingProEnabled;
  const canAddMoreSeats = organizationData ? organizationData.usedSeats < organizationData.features.totalSeats : false;
  const hasActiveSubscription = organizationData?.features.subscriptionStatus === 'active';
  
  logger.debug("Permissions calculated", { 
    canManageMembers, 
    canManageBranding, 
    canAddMoreSeats,
    hasActiveSubscription,
    hasOrgData: !!organizationData 
  });

  return {
    organizationData,
    loading,
    canManageMembers,
    canManageBranding,
    canAddMoreSeats,
    hasActiveSubscription,
    refreshOrganizationData: fetchOrganizationData,
  };
};